import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import PageHeader from '../components/PageHeader';
import { FilterBar, FilterBreak, FilterEnd, FilterSearch } from '../components/FilterBar';
import Appear from '../components/ui/Appear';
import { Alert } from '../components/ui/Alert';
import { AvatarStack } from '../components/ui/Avatar';
import type { AvatarPerson } from '../components/ui/Avatar';
import { Button, buttonClass } from '../components/ui/Button';
import Chip, { ChipGroup } from '../components/ui/Chip';
import { EmptyState } from '../components/ui/EmptyState';
import { IconChevronDown, IconPlus, IconSpark } from '../components/ui/icons';
import { CONTROL_MOTION, EASE, FOCUS, MS, STATE, reducedMotion, useExiting, useFlip, useFocusHandoff, useTicker } from '../components/ui/motion';
import type { Exiting } from '../components/ui/motion';
import FadeIn from '../components/ui/FadeIn';
import PageLayout from '../components/ui/PageLayout';
import { PeekButton, findPeekButton, spaceToPeek } from '../components/ui/Peek';
import Progress from '../components/ui/Progress';
import Reveal from '../components/ui/Reveal';
import SegTrack from '../components/ui/SegTrack';
import Skeleton from '../components/ui/Skeleton';
import { readable, tint } from '../components/ui/Tag';
import Tooltip from '../components/ui/Tooltip';
import { liveScope, useLiveScopes } from '../live/useLiveScopes';
import { listProductions } from './sparkApi';
import type { ProductionRow, TeamSlot } from './sparkApi';
import NewProjectForm, { takeLanded } from './NewProjectForm';
import SparkPeek from './SparkPeek';
import { ReviewStrip, SpiceMeter, StageTag, StatusTag } from './SparkBits';
import { CHANNELS, PIPELINE, REVIEW_STATES, reviewStateOf } from './sparkRules';
import type { Channel, ReviewState, Stage } from './sparkRules';
import { dayText, errorMessage, rowDateText, statusOf, useSparkAccess } from './sparkUi';

type View = 'list' | 'board';

const STAGE_ORDER: Record<string, number> = Object.fromEntries(PIPELINE.map((p, i) => [p.key, i]));

const byStageThenDate = (a: ProductionRow, b: ProductionRow) => {
  const s = (STAGE_ORDER[a.stage] ?? 99) - (STAGE_ORDER[b.stage] ?? 99);
  if (s) return s;
  if (a.is_standing !== b.is_standing) return a.is_standing ? 1 : -1;
  return (a.show_date || '9999').localeCompare(b.show_date || '9999');
};

/** The Review header's sort: furthest behind first, furthest along first, or none. */
type ReviewSort = 'behind' | 'along' | null;
const NEXT_SORT: Record<'behind' | 'along' | 'none', ReviewSort> = { none: 'behind', behind: 'along', along: null };

const byReview = (sort: 'behind' | 'along') => (a: ProductionRow, b: ProductionRow) =>
  (reviewStateOf(a.review.state).rank - reviewStateOf(b.review.state).rank) * (sort === 'behind' ? 1 : -1) || byStageThenDate(a, b);

/** What each Review chip keeps, after "projects": its tooltip and its empty state share the words. */
const REVIEW_KEEPS: Record<ReviewState, string> = {
  not_sent: 'not yet sent for review',
  designer: 'waiting on the designer',
  director: 'waiting on the director',
  producer: 'waiting on the producer',
  client: 'waiting on the client',
  changes: 'with changes asked',
  reviewed: 'that passed review',
};

const readParams = (search: string): { channel: Channel; view: View } => {
  const p = new URLSearchParams(search);
  const channel = CHANNELS.some((c) => c.key === p.get('channel')) ? (p.get('channel') as Channel) : 'shows';
  return { channel, view: p.get('view') === 'board' ? 'board' : 'list' };
};

const help = (
  <div className="space-y-3 text-sm text-on-surface">
    <p>Spark follows every creative project from the queue to the day it flies. Each project sits in one pipeline column and carries its own deliverables, review order and show team.</p>
    <p>List shows one line per project, closest to flying first. Board lays the same projects out by pipeline column, with Wait last. A column with nothing in it stays slim until a project lands there. Each card shows its review progress, with the step it waits on lit.</p>
    <p>Press a pipeline column in the summary, or its part of the bar, to show only that column. Press it again, or Clear, to show every column.</p>
    <p>The Review strip shows where a project's review stands, one mark per reviewer in order: a check has passed, the lit box is waiting now, and the arrow sent it back with changes. With several deliverables in review, it shows the one furthest behind.</p>
    <p>The Review chips show only the projects at one point in review. On a wide screen the list has a Review column: press its heading to sort by how far along each project is, furthest behind first, then furthest along, then back to pipeline order.</p>
    <p>The eye on each project opens a preview without leaving the list. In the preview, the arrows beside its title step to the next and previous project.</p>
    <p>Flown and cancelled projects are hidden until you ask for them.</p>
  </div>
);

// ── Rows that leave ─────────────────────────────────────────────────────────

type Shown = Exiting<ProductionRow>;
const rowKey = (r: ProductionRow) => r.id;

const flipSignature = (shown: Shown[]) => shown.map((s) => `${s.key}:${s.item.stage}:${s.leaving ? 1 : 0}`).join('|');

// The glow blooms and settles on the spring's clock; the hover layer and an exit keep their own.
const ITEM_TRANSITION = `box-shadow ${MS.spring}ms ${EASE.emphasized}, opacity ${MS.exit}ms ${EASE.exit}, background-color ${MS.quick}ms ${EASE.emphasized}, --np-layer ${MS.quick}ms ${EASE.emphasized}`;

// ── Small parts ─────────────────────────────────────────────────────────────

type OnPeek = (row: ProductionRow, from: HTMLElement) => void;

/** A sibling of the row's link, never inside it: a button in an anchor is two controls in one. */
const RowPeek: React.FC<{ row: ProductionRow; onPeek: OnPeek; inert?: boolean }> = ({ row, onPeek, inert }) => (
  <PeekButton name={row.name} peekFor={row.id} inert={inert} onPeek={(from) => onPeek(row, from)} />
);

const teamPeople = (team: TeamSlot[]): AvatarPerson[] =>
  team.filter((t) => t.user_id).map((t): AvatarPerson => ({ key: t.role, name: t.name, tone: t.approved ? 'solid' : 'draft', note: `${t.role}${t.approved ? '' : ', draft'}`, color: t.avatar_color, shape: t.avatar_shape, accessory: t.avatar_accessory, userId: t.user_id }));

const Team: React.FC<{ team: TeamSlot[]; ring?: string }> = ({ team, ring }) => (
  <AvatarStack people={teamPeople(team)} max={3} size="md" ring={ring} empty={<span className="whitespace-nowrap text-xs text-on-surface-variant">No team</span>} />
);

/** "3/7", lit gold once every deliverable is ready. */
const Ready: React.FC<{ row: ProductionRow; className?: string }> = ({ row, className = '' }) => {
  const all = row.deliverables > 0 && row.ready === row.deliverables;
  return (
    <span
      aria-label={`${row.ready} of ${row.deliverables} deliverables ready`}
      className={`whitespace-nowrap tabular-nums transition-colors duration-standard ease-emphasized ${all ? 'font-semibold text-primary drop-shadow-glow' : 'text-on-surface-variant'} ${className}`}
    >
      {row.ready}/{row.deliverables}
    </span>
  );
};

// ── Summary ─────────────────────────────────────────────────────────────────

const Figure: React.FC<{ value: number; label: string; live?: boolean }> = ({ value, label, live }) => {
  const shown = useTicker(value);
  return (
    <div className="flex min-w-0 flex-col-reverse">
      <dt className="truncate text-xs text-on-surface-variant">{label}</dt>
      <dd
        className={`m-0 text-2xl font-bold leading-8 tabular-nums transition-[color,filter] duration-standard ease-emphasized ${
          live ? 'text-primary drop-shadow-glow' : 'text-on-surface'
        }`}
      >
        {shown}
      </dd>
    </div>
  );
};

interface StageFilter { stage: Stage | null; onStage: (stage: Stage | null) => void }

/** A stage's count as a filter key. Inactive stages recede to a fainter tint while one is chosen. */
const StageCount: React.FC<{ stage: Stage; label: string; hex: string; count: number; filter: Stage | null; onStage: (s: Stage | null) => void }> = ({
  stage, label, hex, count, filter, onStage,
}) => {
  const on = count > 0;
  const pressed = filter === stage;
  const receded = filter !== null && !pressed;
  return (
    <li className="flex min-w-0 max-w-full">
      <Tooltip label={pressed ? 'Show every column again' : `Show only ${label}`} note={!on && !pressed ? 'No open projects in this column' : undefined}>
        <Chip
          pressed={pressed}
          count={count}
          hex={hex}
          disabled={!on && !pressed}
          onClick={() => onStage(pressed ? null : stage)}
          className={pressed ? 'shadow-glow' : ''}
          style={pressed ? undefined : {
            backgroundColor: on ? tint(hex, receded ? 0.08 : 0.18) : 'transparent',
            color: on ? readable(hex) : 'var(--on-surface-disabled)',
          }}
        >
          {label}
        </Chip>
      </Tooltip>
    </li>
  );
};

/**
 * The channel at a glance: open projects by stage as one lit bar, then what needs eyes this
 * week. Counted from the list already loaded, never from search, so it holds still while typing.
 * Each stage, in the bar or as a count, filters the list and the board to itself.
 */
const Summary: React.FC<{ rows: ProductionRow[]; channel: Channel } & StageFilter> = ({ rows, channel, stage: filter, onStage }) => {
  const open = rows.filter((r) => r.open);
  const total = open.length;
  const counts = PIPELINE.map((p) => open.filter((r) => r.stage === p.key).length);
  const today = dayjs().format('YYYY-MM-DD');
  const weekEnd = dayjs().add(6, 'day').format('YYYY-MM-DD');
  const soon = open.filter((r) => {
    const d = !r.is_standing && r.show_date ? r.show_date.slice(0, 10) : '';
    return d && d >= today && d <= weekEnd;
  }).length;
  const waiting = open.filter((r) => r.waiting_review).length;
  const ready = open.reduce((n, r) => n + r.ready, 0);
  const deliverables = open.reduce((n, r) => n + r.deliverables, 0);

  // The bar grows from nothing once, when the page first has numbers.
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (typeof requestAnimationFrame !== 'function') { setArmed(true); return undefined; }
    let second = 0;
    const first = requestAnimationFrame(() => { second = requestAnimationFrame(() => setArmed(true)); });
    return () => { cancelAnimationFrame(first); cancelAnimationFrame(second); };
  }, []);

  let start = 0;
  const segments = PIPELINE.map((p, i) => {
    const frac = total ? counts[i] / total : 0;
    const seg = { key: p.key as Stage, hex: p.color, start, frac };
    start += frac;
    return seg;
  });

  return (
    <section aria-label="Pipeline summary" className="mt-5 grid gap-5 rounded-panel bg-surface-container-low p-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center lg:gap-10">
      <div className="min-w-0">
        <div className="flex h-7 items-center justify-between gap-3">
          <h2 className="m-0 text-[13px] font-medium leading-[18px] text-on-surface">Open projects by pipeline</h2>
          <span className="flex items-center gap-2">
            <Appear show={filter !== null} className="inline-flex">
              <Tooltip label="Show every column again">
                <Button variant="quiet" size="sm" onClick={() => onStage(null)}>Clear</Button>
              </Tooltip>
            </Appear>
            <span className="text-xs tabular-nums text-on-surface-variant">{total} open</span>
          </span>
        </div>
        <div className="relative mt-1.5 h-2.5 overflow-hidden rounded-inner bg-surface-container-high shadow-recess">
          {segments.map((s) => {
            const p = PIPELINE.find((x) => x.key === s.key);
            const pressed = filter === s.key;
            return (
              // The counts below are the keyboard path; the bar is the same choice for the pointer.
              <Tooltip key={s.key} label={pressed ? 'Show every column again' : `Show only ${p?.label || ''}`}>
                <button
                  type="button"
                  tabIndex={-1}
                  aria-hidden="true"
                  onClick={() => onStage(pressed ? null : s.key)}
                  className={`absolute inset-0 origin-left ${STATE} ${s.frac === 0 ? 'pointer-events-none' : ''}`}
                  style={{
                    backgroundColor: filter !== null && !pressed ? tint(s.hex, 0.3) : s.hex,
                    transform: `translateX(${(armed ? s.start : 0) * 100}%) scaleX(${armed ? s.frac : 0})`,
                    transition: `${reducedMotion() ? '' : `transform ${MS.spring}ms ${EASE.spring}, `}background-color ${MS.standard}ms ${EASE.emphasized}, --np-layer ${MS.quick}ms ${EASE.emphasized}`,
                  }}
                />
              </Tooltip>
            );
          })}
        </div>
        <ul aria-label="Show only one pipeline column" className="m-0 mt-3 flex list-none flex-wrap gap-1 p-0">
          {PIPELINE.map((p, i) => (
            <StageCount key={p.key} stage={p.key} label={p.label} hex={p.color} count={counts[i]} filter={filter} onStage={onStage} />
          ))}
        </ul>
      </div>
      <dl className="m-0 grid grid-cols-[repeat(2,minmax(0,1fr))] gap-x-6 gap-y-4 sm:grid-cols-[repeat(3,minmax(0,1fr))] lg:flex lg:items-end lg:gap-8">
        <Figure value={soon} label={channel === 'shows' ? 'Flying this week' : 'Due this week'} live={soon > 0} />
        <Figure value={waiting} label="Waiting on a review" />
        <div className="col-span-2 flex min-w-0 flex-col-reverse sm:col-span-1 lg:w-44">
          <dt className="truncate text-xs text-on-surface-variant">Deliverables ready</dt>
          <dd className="m-0 flex h-8 items-center">
            <Progress value={ready} max={deliverables} label="Deliverables ready across open projects" display="count" className="w-full" />
          </dd>
        </div>
      </dl>
    </section>
  );
};

const SummarySkeleton: React.FC = () => (
  <div aria-hidden="true" className="mt-5 grid gap-4 rounded-panel bg-surface-container-low p-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
    <div className="space-y-2.5"><Skeleton className="h-4 w-40" /><Skeleton className="h-2 w-full" /><Skeleton className="h-5 w-3/4" /></div>
    <div className="flex gap-6"><Skeleton className="h-12 flex-1" /><Skeleton className="h-12 flex-1" /><Skeleton className="h-12 flex-1" /></div>
  </div>
);

// ── List ────────────────────────────────────────────────────────────────────

// One template at every width. Phone stacks it as three lines (title; stage and status; date,
// team, spice, ready); from lg the line wrappers dissolve (`lg:contents`) into these columns.
// From xl the free middle takes a Next up column and a Review column follows Status; each is
// display:none below xl and so holds no cell. Below xl the row has no width left for the strip.
const GRID = 'lg:grid-cols-[minmax(0,1fr)_12rem_13rem_5.5rem_5.5rem_3.5rem_3.5rem] xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_12rem_13rem_9.5rem_5.5rem_5.5rem_3.5rem_3.5rem]';

const ymd = (d: dayjs.Dayjs) => d.format('YYYY-MM-DD');

/** What comes next for an open project, from the row already loaded: its preview date, else the deliverable in review. */
const NextUp: React.FC<{ row: ProductionRow }> = ({ row }) => {
  if (!row.open) return <span className="hidden xl:block" />;
  let text = '';
  let tone = 'text-on-surface-variant';
  if (row.preview_due) {
    const due = row.preview_due.slice(0, 10);
    const today = ymd(dayjs());
    if (due < today) { text = `Preview overdue, ${dayText(due, 'MMM D')}`; tone = 'text-red-300'; }
    else {
      text = `Preview due ${dayText(due, 'MMM D')}`;
      if (due <= ymd(dayjs().add(6, 'day'))) tone = 'text-primary';
    }
  } else if (row.waiting_review?.task) {
    text = `${row.waiting_review.task} in review`;
  }
  return <span className={`hidden min-w-0 truncate text-xs tabular-nums xl:block ${tone}`}>{text}</span>;
};
const ROW_OUTER = 'grid grid-cols-[minmax(0,1fr)_2.5rem]';

interface RowProps {
  row: ProductionRow;
  onPeek: OnPeek;
  leaving: boolean;
  landed: boolean;
  edge: { first: boolean; last: boolean };
}

const ProjectRow: React.FC<RowProps> = ({ row, onPeek, leaving, landed, edge }) => {
  const handoff = useFocusHandoff<HTMLDivElement>(leaving);
  return (
  <div ref={handoff} data-flip-key={row.id} className={landed ? 'relative z-[1]' : 'relative'}>
    <div
      aria-hidden={leaving || undefined}
      className={`group ${ROW_OUTER} items-start bg-surface-container-low lg:items-center ${STATE} ${edge.first ? 'rounded-t-panel' : ''} ${edge.last ? 'rounded-b-panel' : ''} ${
        leaving ? 'pointer-events-none opacity-0' : 'opacity-100'
      } ${landed ? 'shadow-glow-soft' : 'shadow-none'}`}
      style={{ transition: ITEM_TRANSITION }}
    >
      <Link
        to={`/spark/projects/${row.id}`}
        tabIndex={leaving ? -1 : undefined}
        data-handoff=""
        onKeyDown={spaceToPeek((from) => onPeek(row, from))}
        className={`grid min-w-0 gap-y-1.5 rounded-panel py-2.5 pl-3 text-sm lg:min-h-[2.5rem] lg:items-center lg:gap-x-3 lg:py-1.5 ${GRID} focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/60`}
      >
        <span className="flex min-w-0 items-baseline gap-2">
          <span className={`truncate ${row.open ? 'font-semibold text-on-surface' : 'font-medium text-on-surface-variant'}`}>{row.name}</span>
          {row.is_standing && <span className="flex-none text-xs text-on-surface-variant">Standing</span>}
          {row.client_name && <span className="hidden min-w-0 truncate text-xs text-on-surface-variant xl:inline">{row.client_name}</span>}
        </span>
        <NextUp row={row} />
        <span className="flex min-w-0 items-center gap-1.5 lg:contents">
          <span className="flex min-w-0 lg:block">{row.open ? <StageTag stage={row.stage} /> : null}</span>
          <span className="flex min-w-0 lg:block"><StatusTag row={row} /></span>
          <span className="hidden min-w-0 items-center xl:flex"><ReviewStrip review={row.review} focusable={false} /></span>
        </span>
        <span className="flex min-w-0 items-center gap-3 text-xs lg:contents">
          <span className="whitespace-nowrap tabular-nums text-on-surface-variant lg:text-right lg:text-sm lg:text-on-surface">{rowDateText(row)}</span>
          <span className="flex min-w-0"><Team team={row.team} /></span>
          <span className="flex"><SpiceMeter level={row.spice} focusable={false} /></span>
          <Ready row={row} className="lg:text-right" />
        </span>
      </Link>
      <span className="justify-self-center pt-1.5 lg:pt-0">
        <RowPeek row={row} onPeek={onPeek} inert={leaving} />
      </span>
    </div>
  </div>
  );
};

const RowStack: React.FC<{ shown: Shown[]; onPeek: OnPeek; landed: string | null }> = ({ shown, onPeek, landed }) => (
  <div className="grid gap-px">
    {shown.map((s, i) => (
      <ProjectRow
        key={s.key}
        row={s.item}
        onPeek={onPeek}
        leaving={s.leaving}
        landed={landed === s.key}
        edge={{ first: i === 0, last: i === shown.length - 1 }}
      />
    ))}
  </div>
);

const SORT_SPOKEN: Record<'behind' | 'along' | 'none', string> = {
  none: 'not sorted', behind: 'sorted furthest behind first', along: 'sorted furthest along first',
};
const SORT_HINT: Record<'behind' | 'along' | 'none', string> = {
  none: 'Back to pipeline order', behind: 'Sort by review, furthest behind first', along: 'Sort by review, furthest along first',
};

/** The Review column's head: each press steps the sort from furthest behind, to furthest along, to none. */
const ReviewSortKey: React.FC<{ sort: ReviewSort; onSort: (next: ReviewSort) => void }> = ({ sort, onSort }) => {
  const next = NEXT_SORT[sort || 'none'];
  return (
    <Tooltip label={SORT_HINT[next || 'none']}>
      <button
        type="button"
        aria-label={`Review, ${SORT_SPOKEN[sort || 'none']}`}
        onClick={() => onSort(next)}
        className={`-mx-1.5 inline-flex h-6 items-center gap-1 rounded-control px-1.5 ${STATE} ${CONTROL_MOTION} ${FOCUS} ${sort ? 'text-on-surface' : ''}`}
      >
        Review
        <IconChevronDown
          className={`h-3 w-3 flex-none transition-[transform,opacity] duration-standard ease-emphasized motion-reduce:transition-[opacity] ${sort ? 'opacity-100' : 'opacity-0'} ${sort === 'behind' ? 'rotate-180' : ''}`}
        />
      </button>
    </Tooltip>
  );
};

const ListView: React.FC<{
  open: ProductionRow[];
  closed: ProductionRow[];
  showClosed: boolean;
  onPeek: OnPeek;
  landed: string | null;
  dateHead: string;
  sort: ReviewSort;
  onSort: (next: ReviewSort) => void;
}> = ({
  open, closed, showClosed, onPeek, landed, dateHead, sort, onSort,
}) => {
  const openShown = useExiting(open, rowKey);
  const closedShown = useExiting(closed, rowKey);
  const frame = useRef<HTMLDivElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  useBoardTop(frame);
  useFlip(root, [flipSignature(openShown), flipSignature(closedShown)]);
  const edge = useScrollEdges(root, body);
  // From sm the list scrolls inside its own box under the page header, like the board; the page
  // itself holds still. The scrollbar is hidden and a glow at an edge says there is more that way.
  return (
    <div ref={frame} className="relative sm:-mx-1.5">
      <div
        ref={root}
        className="sm:h-[max(20rem,calc(100dvh_-_var(--board-top,0px)_-_1rem))] sm:overflow-y-auto sm:overflow-x-hidden sm:overscroll-y-contain sm:px-1.5 sm:pb-1 sm:[scrollbar-width:none] sm:[&::-webkit-scrollbar]:hidden"
      >
      <div ref={body}>
      {/* Sticky to the top of the list's own scroller. aria-hidden goes on each name, never the row:
          the Review head is a key, and a key under aria-hidden still takes focus. */}
      <div className="sticky top-0 z-[2] hidden bg-surface lg:block">
        <div className={`${ROW_OUTER} items-center pb-1.5 pt-2 pl-3 text-[13px] font-medium leading-[18px] text-on-surface-variant`}>
          <div className={`grid items-center gap-x-3 ${GRID}`}>
            <span aria-hidden="true">Project</span>
            <span aria-hidden="true" className="hidden xl:block">Next up</span>
            <span aria-hidden="true">Pipeline</span>
            <span aria-hidden="true">Status</span>
            <span className="hidden xl:block"><ReviewSortKey sort={sort} onSort={onSort} /></span>
            <span aria-hidden="true" className="text-right">{dateHead}</span>
            <span aria-hidden="true">Team</span>
            <span aria-hidden="true">Spice</span>
            <span aria-hidden="true" className="text-right">Ready</span>
          </div>
          <span aria-hidden="true" />
        </div>
      </div>
      {openShown.length > 0 ? (
        <RowStack shown={openShown} onPeek={onPeek} landed={landed} />
      ) : (
        <p className="m-0 rounded-panel bg-surface-container-low px-3 py-3 text-sm text-on-surface-variant">No open projects match.</p>
      )}
      <Reveal show={showClosed && closedShown.length > 0}>
        <h2 className="m-0 px-3 pb-1.5 pt-5 text-[13px] font-medium leading-[18px] text-on-surface-variant">Flown and cancelled</h2>
        <RowStack shown={closedShown} onPeek={onPeek} landed={landed} />
      </Reveal>
      </div>
      </div>
      <div aria-hidden="true" className={`${EDGE_GLOW} top-0 lg:top-9 ${edge.above ? 'opacity-100' : 'opacity-0'}`} style={{ background: glowFrom('top') }} />
      <div aria-hidden="true" className={`${EDGE_GLOW} bottom-0 ${edge.below ? 'opacity-100' : 'opacity-0'}`} style={{ background: glowFrom('bottom') }} />
    </div>
  );
};

// ── Board ───────────────────────────────────────────────────────────────────

/** The translate a running transition has reached, read off the computed matrix. */
const flightOf = (el: HTMLElement): [number, number] => {
  const t = typeof window.getComputedStyle === 'function' ? window.getComputedStyle(el).transform : '';
  if (!t || t === 'none') return [0, 0];
  const n = t.match(/-?[\d.]+(?:e-?\d+)?/g)?.map(Number) || [];
  if (t.startsWith('matrix3d(') && n.length >= 16) return [n[13] || 0, n[14] || 0];
  if (t.startsWith('matrix(') && n.length >= 6) return [n[4] || 0, n[5] || 0];
  return [0, 0];
};

interface Spot { lane: string; x: number; y: number }

/**
 * FLIP for board cards, measured against their lane rather than the board. The kit's useFlip
 * records board positions, and lanes here resize by a CSS width transition after it measures,
 * so its next run would replay that resize as a jump. Inside a lane a card's place holds still
 * while lanes resize; a card changing lane flies from its old lane as that lane stands now.
 * On a phone the lanes stack and jump instead, so they flip too, from where they were recorded.
 */
function useLaneFlip(rootRef: React.RefObject<HTMLElement>, deps: React.DependencyList): void {
  const last = useRef(new Map<string, Spot>());
  const lanesAt = useRef(new Map<string, { x: number; y: number }>());
  const primed = useRef(false);
  const cleanup = useRef<number | undefined>(undefined);
  const unclip = useRef<(() => void) | null>(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    // A flight still in the air has its lanes unclipped; put them back before measuring.
    unclip.current?.();
    unclip.current = null;
    const reduce = reducedMotion();
    const rowMode = typeof window.getComputedStyle === 'function' && window.getComputedStyle(root).flexDirection === 'row';
    const lanes = Array.from(root.querySelectorAll<HTMLElement>('[data-lane]'));
    const cards = Array.from(root.querySelectorAll<HTMLElement>('[data-flip-key]'));
    // Lanes in a row are left alone: an inline transition here would cut their width spring.
    const moving = rowMode ? cards : [...lanes, ...cards];
    const flight = moving.map(flightOf);
    moving.forEach((el) => { el.style.transition = 'none'; el.style.transform = ''; el.style.opacity = ''; });

    const box = root.getBoundingClientRect();
    const at = (el: HTMLElement) => {
      const r = el.getBoundingClientRect();
      return { x: r.left - box.left + root.scrollLeft, y: r.top - box.top + root.scrollTop };
    };
    const laneNow = new Map(lanes.map((l): [string, { x: number; y: number }] => [l.dataset.lane || '', at(l)]));
    const laneFrom = (key: string) => (rowMode ? laneNow.get(key) : lanesAt.current.get(key) || laneNow.get(key));
    // From sm each lane scrolls its own cards, so a card's place is kept in its lane's content
    // coordinates and turned back into a visible one with that lane's scroll as it stands now.
    const scrollers = new Map(lanes.map((l): [string, HTMLElement | null] => [l.dataset.lane || '', l.querySelector<HTMLElement>('[data-lane-scroll]')]));
    const scrollOf = (key: string) => scrollers.get(key)?.scrollTop || 0;
    let crossed = false;

    const plays: Array<() => void> = [];
    const glide = (el: HTMLElement, dx: number, dy: number) => {
      if (reduce || (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5)) return;
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      plays.push(() => {
        el.style.transition = `transform ${MS.spring}ms ${EASE.spring}`;
        el.style.transform = '';
      });
    };

    if (!rowMode && primed.current) {
      lanes.forEach((l, i) => {
        const key = l.dataset.lane || '';
        const was = lanesAt.current.get(key);
        const now = laneNow.get(key);
        if (was && now) glide(l, was.x + flight[i][0] - now.x, was.y + flight[i][1] - now.y);
      });
    }

    const offset = rowMode ? 0 : lanes.length;
    const next = new Map<string, Spot>();
    cards.forEach((el, j) => {
      const key = el.dataset.flipKey || '';
      const laneKey = el.closest<HTMLElement>('[data-lane]')?.dataset.lane || '';
      const lane = laneNow.get(laneKey);
      if (!lane) return;
      const p = at(el);
      const spot = { lane: laneKey, x: p.x - lane.x, y: p.y - lane.y + scrollOf(laneKey) };
      next.set(key, spot);
      const was = last.current.get(key);
      if (was) {
        const from = laneFrom(was.lane);
        const to = laneFrom(laneKey);
        if (!from || !to) return;
        const [fx, fy] = flight[offset + j];
        const dx = from.x + was.x + fx - (to.x + spot.x);
        const dy = from.y + was.y - scrollOf(was.lane) + fy - (to.y + spot.y - scrollOf(laneKey));
        if (was.lane !== laneKey && (Math.abs(dx) >= 0.5 || Math.abs(dy) >= 0.5)) crossed = true;
        glide(el, dx, dy);
      } else if (primed.current) {
        el.style.opacity = '0';
        if (!reduce) el.style.transform = 'scale(0.96)';
        plays.push(() => {
          el.style.transition = `opacity ${MS.standard}ms ${EASE.emphasized}, transform ${MS.spring}ms ${EASE.spring}`;
          el.style.opacity = '';
          el.style.transform = '';
        });
      }
    });
    last.current = next;
    lanesAt.current = laneNow;
    primed.current = true;
    moving.forEach((el) => { if (!el.style.transform && !el.style.opacity) el.style.transition = ''; });

    // A lane's scroller clips sideways, which would cut a card crossing into it off at the lane
    // edge. For the flight each scroller stops scrolling (its content held at the same offset by a
    // translate) and clips top and bottom only, so the card is seen the whole way across.
    if (rowMode && crossed && !reduce) {
      const undo: Array<() => void> = [];
      scrollers.forEach((sc) => {
        const body = sc?.querySelector<HTMLElement>('[data-lane-body]');
        if (!sc || !body) return;
        const top = sc.scrollTop;
        sc.dataset.flying = '1';
        sc.style.overflow = 'visible';
        sc.style.clipPath = 'inset(0px -100vw)';
        body.style.transform = top ? `translateY(${-top}px)` : '';
        undo.push(() => {
          sc.style.overflow = '';
          sc.style.clipPath = '';
          body.style.transform = '';
          sc.scrollTop = top;
          delete sc.dataset.flying;
        });
      });
      unclip.current = () => undo.forEach((u) => u());
    }

    if (plays.length) {
      // Commit the inverted positions before releasing them, or the browser skips straight to the end.
      void root.offsetHeight;
      plays.forEach((p) => p());
    }
    window.clearTimeout(cleanup.current);
    cleanup.current = window.setTimeout(() => {
      moving.forEach((el) => { if (el.isConnected) el.style.transition = ''; });
      unclip.current?.();
      unclip.current = null;
    }, MS.spring + 40);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => () => { window.clearTimeout(cleanup.current); unclip.current?.(); }, []);
}

const observeResize = (cb: () => void): ResizeObserver | null =>
  typeof ResizeObserver === 'function' ? new ResizeObserver(cb) : null;

const rowLaid = (el: HTMLElement) =>
  typeof window.getComputedStyle === 'function' && window.getComputedStyle(el).flexDirection === 'row';

/**
 * From sm the board fills the viewport from its own top down, so the lanes scroll and the page
 * does not. Its top is written to `--board-top` in document coordinates and re-read whenever
 * anything above it changes height (the summary, the filter bar, a form opening) or the window
 * resizes; every ancestor is watched because any of them growing moves the board.
 */
function useBoardTop(rootRef: React.RefObject<HTMLElement>): void {
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const top = root.getBoundingClientRect().top + (window.scrollY || 0);
      root.style.setProperty('--board-top', `${Math.max(0, Math.round(top))}px`);
    };
    const soon = () => { if (!frame) frame = requestAnimationFrame(measure); };
    measure();
    window.addEventListener('resize', soon);
    const ro = observeResize(soon);
    for (let el = root.parentElement; el; el = el.parentElement) ro?.observe(el);
    return () => {
      window.removeEventListener('resize', soon);
      ro?.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/** The board's own bottom padding, sm:pb-2. A horizontal scrollbar takes its place rather than adding to it. */
const BOARD_GUTTER = 8;

/**
 * The board's sideways scroll. While cards and lanes are moving it is held off, since a card in
 * flight or a lane mid-spring briefly reaches past the edge; once they settle it exists only if
 * the lanes really are wider than the board. The scrollbar takes the place of the bottom padding,
 * so the lanes keep their height whether it shows or not.
 */
function useBoardFit(rootRef: React.RefObject<HTMLElement>, signature: string): void {
  const moving = useRef(false);
  const primed = useRef(false);
  const timer = useRef<number | undefined>(undefined);

  const fit = (root: HTMLElement) => {
    if (moving.current) return;
    if (!rowLaid(root)) { root.style.overflowX = ''; root.style.paddingBottom = ''; return; }
    root.style.overflowX = 'hidden';
    root.style.paddingBottom = '';
    if (root.scrollWidth <= root.clientWidth + 1) return;
    root.style.overflowX = 'auto';
    const bar = root.offsetHeight - root.clientHeight;
    root.style.paddingBottom = `${Math.max(0, BOARD_GUTTER - bar)}px`;
  };

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (!primed.current || !rowLaid(root)) { primed.current = true; fit(root); return; }
    moving.current = true;
    root.style.overflowX = 'hidden';
    root.style.paddingBottom = '';
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      moving.current = false;
      if (root.isConnected) fit(root);
    }, MS.spring + 80);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  useEffect(() => {
    const root = rootRef.current;
    const ro = observeResize(() => { if (root) fit(root); });
    if (root) ro?.observe(root, { box: 'border-box' });
    return () => { ro?.disconnect(); window.clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * A lane's head has two faces sharing one slot: the tag and count across the top, and, from sm
 * while the lane is empty and slim, the count over the stage name set on its side. The face not
 * in use is lifted out of flow and faded, so the swap rides the lane's width spring.
 */
const LaneHead: React.FC<{ stage: Stage; count: number; slim: boolean }> = ({ stage, count, slim }) => {
  const shown = useTicker(count);
  const p = PIPELINE.find((x) => x.key === stage);
  // The head itself carries the stage colour: a tint over the opaque panel tone, so a card
  // scrolling beneath never shows through, and the name in the stage's readable ink.
  const fill = p ? { background: `linear-gradient(${tint(p.color, 0.2)}, ${tint(p.color, 0.2)}), var(--surface-container)` } : undefined;
  const ink = p ? readable(p.color) : undefined;
  const countStyle = { color: ink, opacity: count > 0 ? 0.75 : 0.45 };
  return (
    <div className="sticky top-0 z-[2] flex-none bg-surface pb-2 sm:pb-1">
      <div className="relative">
        <div
          aria-hidden="true"
          style={fill}
          className={`flex h-9 items-center justify-between gap-2 overflow-hidden rounded-control bg-surface-container px-3 transition-opacity duration-standard ease-emphasized ${
            slim ? 'sm:pointer-events-none sm:absolute sm:inset-x-0 sm:top-0 sm:opacity-0' : 'opacity-100'
          }`}
        >
          <span className="min-w-0 truncate text-[13px] font-semibold leading-5" style={{ color: ink }}>{p?.label || stage}</span>
          <span className="flex-none text-xs font-medium tabular-nums" style={countStyle}>{shown}</span>
        </div>
        <div
          aria-hidden="true"
          style={fill}
          className={`hidden flex-col items-center gap-2 overflow-hidden rounded-control bg-surface-container pb-3 pt-2 transition-opacity duration-standard ease-emphasized sm:flex ${
            slim ? 'opacity-100' : 'pointer-events-none absolute inset-x-0 top-0 opacity-0'
          }`}
        >
          <span className="text-xs font-medium tabular-nums" style={countStyle}>{shown}</span>
          {p && (
            <span className="rotate-180 whitespace-nowrap text-xs font-semibold leading-4 [writing-mode:vertical-rl]" style={{ color: ink }}>
              {p.label}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/** Which of a card's keys held focus as the card left its lane. */
interface CarriedFocus { key: string; at: number }
const CARD_KEYS = 'a[href], button';
const cardKeys = (card: Element) => Array.from(card.querySelectorAll<HTMLElement>(CARD_KEYS));

const BoardCard: React.FC<{ row: ProductionRow; onPeek: OnPeek; leaving: boolean; landed: boolean; carry: React.MutableRefObject<CarriedFocus | null> }> = ({
  row, onPeek, leaving, landed, carry,
}) => {
  const handoff = useFocusHandoff<HTMLDivElement>(leaving);
  // A lane change mounts a new card in the new lane, so the focused node goes with the old one.
  // A layout cleanup runs before React removes that node: it is the last moment focus is readable.
  useLayoutEffect(() => {
    const el = handoff.current;
    return () => {
      const at = el ? cardKeys(el).indexOf(document.activeElement as HTMLElement) : -1;
      if (at >= 0) carry.current = { key: row.id, at };
    };
  }, [handoff, carry, row.id]);
  return (
  <div ref={handoff} data-flip-key={row.id} className="relative min-w-0">
    {/* Clips its content, not its own glow: a card in a lane still springing open is wiped into view. */}
    <div
      aria-hidden={leaving || undefined}
      className={`group relative min-w-0 overflow-hidden rounded-control bg-surface-container-low ${STATE} ${leaving ? 'pointer-events-none opacity-0' : 'opacity-100'} ${
        landed ? 'shadow-glow-soft' : 'shadow-none hover:shadow-glow-soft'
      }`}
      style={{ transition: ITEM_TRANSITION }}
    >
      <Link
        to={`/spark/projects/${row.id}`}
        tabIndex={leaving ? -1 : undefined}
        data-handoff=""
        onKeyDown={spaceToPeek((from) => onPeek(row, from))}
        className="block rounded-control px-3 pb-2 pt-2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/60 sm:min-w-[16rem]"
      >
        <span className="line-clamp-2 text-sm font-semibold leading-5 text-on-surface">{row.name}</span>
        <span className="mt-1.5 flex min-w-0 flex-wrap items-center justify-between gap-x-2 gap-y-1.5">
          {statusOf(row) ? <StatusTag row={row} /> : <span className="text-xs text-on-surface-variant">No status</span>}
          <ReviewStrip review={row.review} focusable={false} align="end" className="ml-auto" />
        </span>
        <span className="mt-2 flex h-8 min-w-0 items-center gap-2 pr-8 text-xs">
          <span className="flex-none whitespace-nowrap tabular-nums text-on-surface-variant">{rowDateText(row)}</span>
          <Team team={row.team} />
          <SpiceMeter level={row.spice} focusable={false} />
          <Ready row={row} className="ml-auto" />
        </span>
      </Link>
      <span className="absolute bottom-2.5 right-1.5">
        <RowPeek row={row} onPeek={onPeek} inert={leaving} />
      </span>
    </div>
  </div>
  );
};

// Every stage keeps its lane, in pipeline order. A lane with cards takes 16 to 24rem; an empty
// one slims to a spine, and the width springs either way. Phone stacks the lanes and uses the page.
const LANE = 'min-w-0 sm:flex sm:flex-col sm:transition-[flex-grow,flex-basis,min-width,max-width] sm:duration-spring sm:ease-spring sm:motion-reduce:transition-none';

// The lane's light at an edge with more cards past it: gold rising from the edge, not a grey fade.
// Inset by the scroller's own outward margin (sm:-mx-1.5), so the light lines up with the lane head above.
const EDGE_GLOW = 'pointer-events-none absolute inset-x-0 sm:inset-x-1.5 rounded-control z-[1] hidden h-7 transition-opacity duration-standard ease-emphasized motion-reduce:transition-none sm:block';
const glowFrom = (side: 'top' | 'bottom') =>
  `linear-gradient(to ${side === 'bottom' ? 'top' : 'bottom'}, color-mix(in srgb, var(--color-primary, #ffcc33) 16%, transparent), transparent)`;

/**
 * A lane's cards. From sm they scroll on their own with the scrollbar hidden, and a glow at the
 * top or bottom edge says there is more that way. Its margin and padding cancel, which leaves
 * the cards where they were and gives their hover glow room inside the clip.
 */
/** Whether a scroller has more content above or below what it shows, for its edge glows. */
function useScrollEdges(scrollRef: React.RefObject<HTMLElement>, bodyRef: React.RefObject<HTMLElement>) {
  const [edge, setEdge] = useState({ above: false, below: false });
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    let frame = 0;
    const read = () => {
      frame = 0;
      // Mid-flight a lane is unclipped and reads as unscrolled; the reading before it stands.
      if (el.dataset.flying) return;
      const above = el.scrollTop > 4;
      const below = el.scrollTop + el.clientHeight < el.scrollHeight - 4;
      setEdge((e) => (e.above === above && e.below === below ? e : { above, below }));
    };
    const soon = () => { if (!frame) frame = requestAnimationFrame(read); };
    read();
    el.addEventListener('scroll', soon, { passive: true });
    const ro = observeResize(soon);
    ro?.observe(el);
    if (bodyRef.current) ro?.observe(bodyRef.current);
    return () => {
      el.removeEventListener('scroll', soon);
      ro?.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return edge;
}

const LaneBody: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const scroller = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const edge = useScrollEdges(scroller, body);

  return (
    <div className="relative min-h-0 sm:-mx-1.5 sm:flex-1">
      <div
        ref={scroller}
        data-lane-scroll=""
        className="sm:h-full sm:overflow-y-auto sm:overflow-x-hidden sm:overscroll-y-contain sm:px-1.5 sm:py-1 sm:[scrollbar-width:none] sm:[&::-webkit-scrollbar]:hidden"
      >
        <div ref={body} data-lane-body="" className="grid grid-cols-1 gap-2">
          {children}
        </div>
      </div>
      <div aria-hidden="true" className={`${EDGE_GLOW} top-0 ${edge.above ? 'opacity-100' : 'opacity-0'}`} style={{ background: glowFrom('top') }} />
      <div aria-hidden="true" className={`${EDGE_GLOW} bottom-0 ${edge.below ? 'opacity-100' : 'opacity-0'}`} style={{ background: glowFrom('bottom') }} />
    </div>
  );
};
const LANE_WIDE = 'sm:min-w-[16rem] sm:max-w-[24rem] sm:flex-[1_1_16rem]';
const LANE_SLIM = 'sm:min-w-[3.75rem] sm:max-w-[3.75rem] sm:flex-[0_0_3.75rem]';

const BoardView: React.FC<{ open: ProductionRow[]; onPeek: OnPeek; landed: string | null }> = ({ open, onPeek, landed }) => {
  const shown = useExiting(open, rowKey);
  const root = useRef<HTMLDivElement>(null);
  const cols = PIPELINE.map((p) => ({ ...p, items: shown.filter((s) => s.item.stage === (p.key as Stage)) }));
  const signature = flipSignature(shown);
  useBoardTop(root);
  useBoardFit(root, signature);
  useLaneFlip(root, [signature]);
  // Every commit, after the cards' own effects: a card that changed lane takes back the key its
  // old self held, with no scroll, so focus rides the glide. Unclaimed, the note is dropped.
  const carry = useRef<CarriedFocus | null>(null);
  useLayoutEffect(() => {
    const c = carry.current;
    carry.current = null;
    const card = c && Array.from(root.current?.querySelectorAll<HTMLElement>('[data-flip-key]') || []).find((el) => el.dataset.flipKey === c.key);
    if (c && card) cardKeys(card)[c.at]?.focus({ preventScroll: true });
  });

  // The 1rem under the board is the page's own bottom padding in board view (SparkPage), so
  // the two together end exactly at the viewport's bottom. The floor keeps a short window usable.
  return (
    <div
      ref={root}
      className="flex flex-col gap-y-4 overscroll-x-contain pb-2 sm:h-[max(20rem,calc(100dvh_-_var(--board-top,0px)_-_1rem))] sm:flex-row sm:items-stretch sm:gap-x-3 sm:overflow-x-hidden sm:overflow-y-hidden"
    >
      {cols.map((c) => {
        const live = c.items.filter((s) => !s.leaving).length;
        const slim = c.items.length === 0;
        return (
          <section key={c.key} data-lane={c.key} aria-label={`${c.label}, ${live}`} className={`${LANE} ${slim ? LANE_SLIM : LANE_WIDE}`}>
            <LaneHead stage={c.key} count={live} slim={slim} />
            <LaneBody>
              {c.items.map((s) => (
                <BoardCard key={s.key} row={s.item} onPeek={onPeek} leaving={s.leaving} landed={landed === s.key} carry={carry} />
              ))}
            </LaneBody>
          </section>
        );
      })}
    </div>
  );
};

const ListSkeleton: React.FC = () => (
  <div aria-busy="true" aria-label="Loading projects" className="grid gap-px">
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <div key={i} className={`flex items-center gap-4 bg-surface-container-low px-3 py-3 ${i === 0 ? 'rounded-t-panel' : ''} ${i === 5 ? 'rounded-b-panel' : ''}`}>
        <Skeleton className="h-4 flex-1" /><Skeleton className="h-5 w-28 rounded-full" /><Skeleton className="hidden h-4 w-20 sm:block" />
      </div>
    ))}
  </div>
);

const countText = (open: number, closed: number, showClosed: boolean) => `${open} open${showClosed ? `, ${closed} closed` : ''}`;

/** The ticking figure is for the eye; a screen reader hears only where it lands. */
const OpenCount: React.FC<{ open: number; closed: number; showClosed: boolean }> = ({ open, closed, showClosed }) => {
  const o = useTicker(open);
  const c = useTicker(closed);
  return (
    <>
      <span aria-hidden="true">{countText(o, c, showClosed)}</span>
      <span aria-live="polite" className="sr-only">{countText(open, closed, showClosed)}</span>
    </>
  );
};

// ── Page ────────────────────────────────────────────────────────────────────

const SparkPage: React.FC = () => {
  const { studio } = useSparkAccess();
  const history = useHistory();
  const location = useLocation();
  const { channel, view } = readParams(location.search);

  const [showClosed, setShowClosed] = useState(false);
  const [stage, setStage] = useState<Stage | null>(null);
  const [review, setReview] = useState<ReviewState | null>(null);
  const [reviewSort, setReviewSort] = useState<ReviewSort>(null);
  const [peek, setPeek] = useState<{ id: string; name: string } | null>(null);
  const peekFrom = useRef<{ node: HTMLElement; id: string } | null>(null);
  // Resolved when the peek closes: a card that changed lane is a new node, so the remembered one may be detached.
  const peekReturn = useMemo<React.RefObject<HTMLElement | null>>(() => ({
    get current() {
      const from = peekFrom.current;
      if (!from) return null;
      if (from.node.isConnected) return from.node;
      if (from.node.hasAttribute('data-peek-for')) return findPeekButton(from.id);
      return document.querySelector(`[data-flip-key="${from.id}"]`)?.querySelector<HTMLElement>('a[data-handoff]') ?? null;
    },
  }), []);
  const openPeek = useCallback((row: ProductionRow, from: HTMLElement) => {
    peekFrom.current = { node: from, id: row.id };
    setPeek({ id: row.id, name: row.name });
  }, []);
  const closePeek = useCallback(() => setPeek(null), []);
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [rows, setRows] = useState<ProductionRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [landed, setLanded] = useState<string | null>(null);

  const setParam = (key: 'channel' | 'view', value: string) => {
    const p = new URLSearchParams(location.search);
    p.set(key, value);
    history.replace({ search: p.toString() });
  };

  // A reload keeps the last rows on screen, dimmed, so a channel switch moves cards rather
  // than blanking the page; only the first load shows the skeleton.
  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    listProductions(channel, showClosed)
      .then((r) => { if (live) { setRows(r.productions); setLoading(false); } })
      .catch((e) => { if (live) { setError(errorMessage(e, 'Could not load projects.')); setLoading(false); } });
    return () => { live = false; };
  }, [channel, showClosed, reloadKey]);

  // Someone else's change: refetch quietly, no dimming. Cards move through the list's own animations.
  // A failure leaves the list as it is; the rejection tells the poller to try again.
  const query = useRef({ channel, showClosed });
  query.current = { channel, showClosed };
  useLiveScopes([liveScope.spark], () => {
    const asked = { channel, showClosed };
    return listProductions(asked.channel, asked.showClosed)
      .then((r) => {
        if (query.current.channel !== asked.channel || query.current.showClosed !== asked.showClosed) return;
        setRows(r.productions);
        setError(null);
      });
  });

  // A project created moments ago lights up once when it lands here, then settles.
  useEffect(() => {
    if (!rows || loading) return undefined;
    const id = takeLanded(rows.map((r) => r.id));
    if (!id) return undefined;
    setLanded(id);
    const scroll = window.setTimeout(() => {
      document.querySelector(`[data-flip-key="${id}"]`)?.scrollIntoView?.({ block: 'nearest' });
    }, 50);
    const settle = window.setTimeout(() => setLanded(null), 1600);
    return () => { window.clearTimeout(scroll); window.clearTimeout(settle); };
  }, [rows, loading]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const all = rows || [];
    const hit = (r: ProductionRow) => {
      if (!needle) return true;
      const st = statusOf(r);
      const stage = PIPELINE.find((p) => p.key === r.stage)?.label || '';
      return [r.name, r.client_name, stage, st?.label, reviewStateOf(r.review.state).label, ...r.team.map((t) => t.name)]
        .some((s) => (s || '').toLowerCase().includes(needle));
    };
    const found = all.filter((r) => hit(r) && (!stage || r.stage === stage));
    // The chips count what this view would show under each of them; the board has no closed rows.
    const counted = view === 'list' && showClosed ? found : found.filter((r) => r.open);
    const reviewCounts = new Map<ReviewState, number>();
    counted.forEach((r) => reviewCounts.set(r.review.state, (reviewCounts.get(r.review.state) || 0) + 1));
    const kept = review ? found.filter((r) => r.review.state === review) : found;
    const order = view === 'list' && reviewSort ? byReview(reviewSort) : byStageThenDate;
    return {
      open: kept.filter((r) => r.open).sort(order),
      closed: kept.filter((r) => !r.open).sort(order),
      total: all.length,
      counted: counted.length,
      reviewCounts,
    };
  }, [rows, q, stage, review, reviewSort, view, showClosed]);

  // The order the peek's arrows step through: what the screen shows, top to bottom.
  const order = useMemo(
    () => (view === 'board' || !showClosed ? visible.open : [...visible.open, ...visible.closed]),
    [view, showClosed, visible],
  );
  const peekIndex = peek ? order.findIndex((r) => r.id === peek.id) : -1;
  const stepPeek = useCallback((dir: 1 | -1) => {
    if (!peek || order.length === 0) return;
    const at = order.findIndex((r) => r.id === peek.id);
    const next = order[((at < 0 ? 0 : at + dir) + order.length) % order.length];
    const btn = findPeekButton(next.id);
    if (btn) peekFrom.current = { node: btn, id: next.id };
    setPeek({ id: next.id, name: next.name });
  }, [peek, order]);

  const chan = CHANNELS.find((c) => c.key === channel);
  const searching = q.trim() !== '';
  const stageLabel = stage ? PIPELINE.find((p) => p.key === stage)?.label || '' : '';
  const empty = rows !== null && (view === 'board' ? visible.open.length === 0 : visible.open.length === 0 && visible.closed.length === 0);
  const dateHead = channel === 'shows' ? 'Show date' : 'Due date';

  return (
    <PageLayout width="full"
      kind="workspace"
      className="sm:pb-4"
      header={(
        <PageHeader
          title="Spark"
          icon={<IconSpark />}
          lede={chan?.lede}
          help={help}
          primary={studio ? (
            <Tooltip label="Start a project">
              <Button variant="filled" leading={<IconPlus className="h-3.5 w-3.5" />} onClick={() => setCreating(true)}>New project</Button>
            </Tooltip>
          ) : undefined}
          actions={(
            <>
              <Tooltip label="Your deliverables, reviews and timers">
                <Link to="/spark/me" className={buttonClass('tonal')}>My work</Link>
              </Tooltip>
              <Tooltip label="The roster, workload and resources">
                <Link to="/spark/studio" className={buttonClass('tonal')}>Studio</Link>
              </Tooltip>
            </>
          )}
        />
      )}
    >
      <NewProjectForm open={creating && studio} defaultChannel={channel} onCancel={() => setCreating(false)} />

      {rows === null && !error ? <SummarySkeleton /> : rows ? <Summary rows={rows} channel={channel} stage={stage} onStage={setStage} /> : null}

      <FilterBar className="mt-4" label="Projects">
        <SegTrack
          label="Channel"
          value={channel}
          onChange={(k) => setParam('channel', k)}
          options={CHANNELS.map((c) => ({ key: c.key, label: c.label }))}
        />
        <SegTrack
          label="View"
          value={view}
          onChange={(k) => setParam('view', k)}
          options={[{ key: 'list', label: 'List' }, { key: 'board', label: 'Board' }]}
        />
        <FilterSearch id="spark-search" value={q} onChange={setQ} label="Search projects by name, client, pipeline, status or team" placeholder="Search projects" />
        <Chip pressed={showClosed} onClick={() => setShowClosed((v) => !v)}>Show flown and cancelled</Chip>
        <FilterEnd>
          {rows !== null && <OpenCount open={visible.open.length} closed={visible.closed.length} showClosed={showClosed} />}
        </FilterEnd>
        <FilterBreak />
        <ChipGroup label="Review">
          <span aria-hidden="true" className="mr-0.5 text-xs text-on-surface-variant">Review</span>
          <Tooltip label={review ? 'Show every review status again' : ''}>
            <Chip pressed={review === null} count={rows ? visible.counted : undefined} onClick={() => setReview(null)}>All</Chip>
          </Tooltip>
          {REVIEW_STATES.map((s) => {
            const pressed = review === s.key;
            const n = visible.reviewCounts.get(s.key) || 0;
            const none = !!rows && n === 0 && !pressed;
            return (
              <Tooltip
                key={s.key}
                label={pressed ? 'Show every review status again' : `Show only projects ${REVIEW_KEEPS[s.key]}`}
                note={none ? 'No projects here right now' : undefined}
              >
                <Chip pressed={pressed} count={rows ? n : undefined} disabled={none} onClick={() => setReview(pressed ? null : s.key)}>
                  {s.chip}
                </Chip>
              </Tooltip>
            );
          })}
        </ChipGroup>
      </FilterBar>

      <div
        aria-busy={loading || undefined}
        className={`mt-3 transition-opacity ${loading && rows ? 'opacity-60 duration-standard ease-emphasized' : 'opacity-100 duration-standard ease-emphasized'}`}
      >
        {error ? (
          <Alert tone="error" onRetry={() => setReloadKey((k) => k + 1)}>{`${error} Check your connection and try again.`}</Alert>
        ) : rows === null ? (
          <ListSkeleton />
        ) : empty ? (
          searching ? (
            <EmptyState
              action={(
                <Tooltip label="Show every project again">
                  <Button variant="tonal" onClick={() => setQ('')}>Clear search</Button>
                </Tooltip>
              )}
            >
              Nothing in {chan?.label} matches &ldquo;{q.trim()}&rdquo;.
            </EmptyState>
          ) : review ? (
            <EmptyState
              action={(
                <Tooltip label="Show every review status again">
                  <Button variant="tonal" onClick={() => setReview(null)}>Show all</Button>
                </Tooltip>
              )}
            >
              No {chan?.label} projects{stage ? ` in ${stageLabel}` : ''} {REVIEW_KEEPS[review]} right now.
            </EmptyState>
          ) : stage ? (
            <EmptyState
              action={(
                <Tooltip label="Show every column again">
                  <Button variant="tonal" onClick={() => setStage(null)}>Clear</Button>
                </Tooltip>
              )}
            >
              Nothing in {chan?.label} is in {stageLabel} right now.
            </EmptyState>
          ) : (
            <EmptyState
              title={visible.total === 0 && !showClosed ? `No ${chan?.label} projects yet.` : `No open ${chan?.label} projects.`}
              action={studio ? (
                <Button variant="filled" leading={<IconPlus className="h-3.5 w-3.5" />} onClick={() => setCreating(true)}>New project</Button>
              ) : undefined}
            >
              {studio
                ? 'Start one with New project. You can link an event now or whenever it is booked.'
                : 'Projects show up here as soon as the Studio starts them.'}
            </EmptyState>
          )
        ) : view === 'board' ? (
          // Keyed by view only: across a channel switch the same board stays, so cards fade and lanes spring.
          <FadeIn key="board">
            <BoardView open={visible.open} onPeek={openPeek} landed={landed} />
          </FadeIn>
        ) : (
          <FadeIn key="list">
            <ListView
              open={visible.open}
              closed={visible.closed}
              showClosed={showClosed}
              onPeek={openPeek}
              landed={landed}
              dateHead={dateHead}
              sort={reviewSort}
              onSort={setReviewSort}
            />
          </FadeIn>
        )}
      </div>
      <SparkPeek
        id={peek?.id ?? null}
        name={peek?.name ?? ''}
        onClose={closePeek}
        returnTo={peekReturn}
        onStep={stepPeek}
        position={peekIndex >= 0 ? { index: peekIndex, total: order.length } : undefined}
      />
    </PageLayout>
  );
};

export default SparkPage;
